import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppMode } from '@repo/frontend-types';

interface SubscriptionRef {
  unsubscribe: () => void;
}

interface UseSubscriptionHealthCheckOptions {
  appMode: AppMode;
  subscriptionRef: React.MutableRefObject<SubscriptionRef | null>;
  lastDataReceivedRef: React.MutableRefObject<number | null>;
  subscriptionCreatedAtRef: React.MutableRefObject<number | null>;
  setLiveData: (liveData: any) => void;
  isActive: boolean; // Whether the subscription should be active (e.g., apolloClient and dgraph_namespace available)
}

export interface SubscriptionHealthCheck {
  /** Bumped to force the live-subscription effect to recreate its subscription. */
  reconnectTrigger: number;
  /**
   * Report that the live subscription errored. Schedules ONE reconnection attempt under bounded
   * exponential backoff. Stable identity across renders — see `latestRef`.
   */
  reportSubscriptionError: () => void;
  /** Report that live data arrived: clears the backoff timer AND resets the attempt counter. */
  resetSubscriptionBackoff: () => void;
  /**
   * Report that a subscription now exists, so a retry scheduled for its absence is moot. Clears the
   * pending timer but KEEPS the attempt counter, so a feed that keeps breaking still backs off.
   */
  cancelPendingRetry: () => void;
}

const CONNECTION_TIMEOUT_MS = 300000; // 5 minutes - if no data received, consider connection dead (data arrives every 2-3 mins)
const INITIAL_DATA_TIMEOUT_MS = 300000; // 5 minutes - if no initial data received, consider connection dead
const HEALTH_CHECK_INTERVAL_MS = 60000; // Check every 60 seconds
const BACKOFF_BASE_MS = 5000; // First retry after a subscription error
const BACKOFF_CAP_MS = 60000; // Never wait longer than this between retries

const isLiveSubscriptionMode = (appMode: AppMode) =>
  appMode === AppMode.LIVE || appMode === AppMode.OPERATIONS;

/**
 * Custom hook that monitors subscription health and automatically reconnects dead connections.
 * Handles cases where WebSocket connections silently die (e.g., tab inactive for long time), and
 * cases where the subscription errored outright and so no longer exists at all.
 *
 * This hook owns the ONE reconnection mechanism in the live-data path. Three call sites converge
 * on it: the 60s interval, the visibilitychange handler, and DataContext's subscription error
 * handler (via `reportSubscriptionError`). There is deliberately a single `reconnectTrigger`
 * state — a second trigger would let two paths race to recreate the same subscription.
 *
 * @param options - Configuration for health monitoring
 * @returns the reconnect trigger plus the error/success reporters
 */
export const useSubscriptionHealthCheck = (
  options: UseSubscriptionHealthCheckOptions,
): SubscriptionHealthCheck => {
  const {
    appMode,
    subscriptionRef,
    lastDataReceivedRef,
    subscriptionCreatedAtRef,
    setLiveData,
    isActive,
  } = options;
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  /*
   * Backoff state for retries after a subscription error. `timer` doubles as the
   * "a retry is already in flight" flag: while it is set, neither another error report nor a
   * health-check tick may schedule or force a second reconnection (NFR2 — no reconnection storms).
   */
  const backoffRef = useRef<{ attempt: number; timer: ReturnType<typeof setTimeout> | null }>({
    attempt: 0,
    timer: null,
  });
  /*
   * `appMode` and `isActive` are read through a ref rather than closed over, so that
   * `reportSubscriptionError` and `resetSubscriptionBackoff` keep a STABLE identity across
   * renders. DataContext's live-subscription effect must not re-run when the app mode changes:
   * the subscription runs in the background across modes, and tearing it down on every mode switch
   * would recreate the websocket every time a user moves between Live and Historical.
   */
  const latestRef = useRef({ isActive, appMode });
  /*
   * useLayoutEffect, NOT useEffect, and for the same reason DataContext uses one for `appModeRef`:
   * passive effects are flushed in a scheduler macrotask AFTER commit, so a timer or a
   * visibilitychange landing in that window would read the previous commit's mode and act on a state
   * the session has already left. A layout effect runs synchronously during commit, closing it.
   */
  useLayoutEffect(() => {
    latestRef.current = { isActive, appMode };
  }, [isActive, appMode]);

  const clearBackoffTimer = useCallback(() => {
    if (backoffRef.current.timer) {
      clearTimeout(backoffRef.current.timer);
      backoffRef.current.timer = null;
    }
  }, []);

  /**
   * Force reconnection by unsubscribing dead subscription and triggering recreation
   */
  const forceReconnection = useCallback(() => {
    if (subscriptionRef.current) {
      try {
        subscriptionRef.current.unsubscribe();
      } catch (error) {
        console.warn('Error unsubscribing dead subscription:', error);
      }
      subscriptionRef.current = null;
    }
    setLiveData(null);
    lastDataReceivedRef.current = null;
    subscriptionCreatedAtRef.current = null;
    setReconnectTrigger(prev => prev + 1);
  }, [setLiveData, subscriptionRef, lastDataReceivedRef, subscriptionCreatedAtRef]);

  /**
   * A subscription error used to be terminal: the error handler nulled the subscription ref and
   * every watchdog below early-returned on a null ref, so one lost boot race left a tab with no
   * live data for its whole life. Schedule a bounded retry instead.
   */
  const reportSubscriptionError = useCallback(() => {
    const { isActive: currentlyActive, appMode: currentMode } = latestRef.current;

    // Only retry while the subscription SHOULD be active. An org with no dgraph namespace, or a
    // session that has left live mode, must not spin on a retry loop.
    if (!currentlyActive || !isLiveSubscriptionMode(currentMode)) {
      return;
    }

    // A retry is already scheduled — at most one in-flight attempt at a time.
    if (backoffRef.current.timer) {
      return;
    }

    const delay = Math.min(BACKOFF_BASE_MS * 2 ** backoffRef.current.attempt, BACKOFF_CAP_MS);
    backoffRef.current.attempt += 1;

    console.warn(
      `useSubscriptionHealthCheck: subscription errored, reconnecting in ${Math.round(delay / 1000)}s (attempt ${backoffRef.current.attempt})`,
    );

    backoffRef.current.timer = setTimeout(() => {
      backoffRef.current.timer = null;
      const { isActive: stillActive, appMode: modeNow } = latestRef.current;
      if (!stillActive || !isLiveSubscriptionMode(modeNow)) {
        return;
      }
      forceReconnection();
    }, delay);
  }, [forceReconnection]);

  /**
   * Report that live data arrived. Success — not a reconnection attempt — is what resets the
   * backoff, so a feed that recovers starts from the 5s baseline the next time it breaks.
   */
  const resetSubscriptionBackoff = useCallback(() => {
    clearBackoffTimer();
    backoffRef.current.attempt = 0;
  }, [clearBackoffTimer]);

  /**
   * A subscription exists again, by some path other than data arriving — so drop a retry that was
   * scheduled for its absence. The attempt counter is deliberately preserved: if this new
   * subscription also errors, the backoff must continue growing rather than restart at 5s.
   */
  const cancelPendingRetry = useCallback(() => {
    clearBackoffTimer();
  }, [clearBackoffTimer]);

  /**
   * Check if connection is dead and force reconnection if needed
   */
  const checkConnectionHealth = useCallback(() => {
    const now = Date.now();
    const lastReceived = lastDataReceivedRef.current;
    const subscriptionCreatedAt = subscriptionCreatedAtRef.current;

    if (!subscriptionRef.current) {
      // Not "no subscription to check" — while the feed SHOULD be live, a null ref IS the dead
      // connection. This is the backstop for a scheduled retry that never ran (an effect cleanup
      // race, a timer cancelled by a mode change), and the reason a lost boot race used to be
      // permanent: the watchdogs only ever rescued subscriptions that existed and went quiet.
      const { isActive: currentlyActive, appMode: currentMode } = latestRef.current;
      if (currentlyActive && isLiveSubscriptionMode(currentMode) && !backoffRef.current.timer) {
        console.warn(
          'useSubscriptionHealthCheck: live mode is active but no subscription exists, forcing reconnection',
        );
        forceReconnection();
      }
      return;
    }

    // Case 1: We've received data before, but not recently - connection likely died
    if (lastReceived !== null && now - lastReceived > CONNECTION_TIMEOUT_MS) {
      console.warn(
        `useSubscriptionHealthCheck: No data received for ${Math.round((now - lastReceived) / 1000)}s, forcing subscription reconnection`,
      );
      forceReconnection();
    }
    // Case 2: Subscription created but never received initial data - connection likely dead
    else if (
      lastReceived === null &&
      subscriptionCreatedAt !== null &&
      now - subscriptionCreatedAt > INITIAL_DATA_TIMEOUT_MS
    ) {
      console.warn(
        `useSubscriptionHealthCheck: Subscription created ${Math.round((now - subscriptionCreatedAt) / 1000)}s ago but never received data, forcing reconnection`,
      );
      forceReconnection();
    }
  }, [forceReconnection, subscriptionRef, lastDataReceivedRef, subscriptionCreatedAtRef]);

  /**
   * Health check interval - runs periodically to detect dead connections
   */
  useEffect(() => {
    if (isActive && isLiveSubscriptionMode(appMode)) {
      // OPERATIONS runs the same live dGraph subscription as LIVE (replace-embeddable R1), so it
      // needs the same health-check/reconnect guard.
      // Start health check interval
      healthCheckIntervalRef.current = setInterval(() => {
        checkConnectionHealth();
      }, HEALTH_CHECK_INTERVAL_MS);

      return () => {
        if (healthCheckIntervalRef.current) {
          clearInterval(healthCheckIntervalRef.current);
          healthCheckIntervalRef.current = null;
        }
      };
    } else {
      // Clear health check when not in live mode or not active
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
    }
  }, [isActive, appMode, checkConnectionHealth]);

  /**
   * Handle visibility change to check connection health when tab becomes active
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isLiveSubscriptionMode(appMode) && isActive) {
        const now = Date.now();
        const lastReceived = lastDataReceivedRef.current;
        const subscriptionCreatedAt = subscriptionCreatedAtRef.current;

        if (!subscriptionRef.current) {
          /*
           * Same reasoning as the interval above: a null ref in live mode is a dead feed. Unlike the
           * interval, this path does NOT defer to a pending retry — it cancels it and reconnects at
           * once. A dead feed means no open websocket, and a hidden tab with no socket has its
           * timers throttled to ~1-minute alignment, so the 5s retry armed before the tab was hidden
           * may still be unfired when the user comes back. The moment they are looking at it is the
           * moment to be aggressive, and cancelling first keeps this to one attempt.
           */
          cancelPendingRetry();
          console.warn(
            'useSubscriptionHealthCheck: Tab became visible with no subscription in live mode, forcing reconnection',
          );
          forceReconnection();
          return;
        }

        // Case 1: We've received data before, but not recently - connection likely died
        if (lastReceived !== null && now - lastReceived > CONNECTION_TIMEOUT_MS) {
          console.warn(
            'useSubscriptionHealthCheck: Tab became visible but no recent data received, forcing subscription reconnection',
          );
          forceReconnection();
        }
        // Case 2: Subscription created but never received initial data - connection likely dead
        else if (
          lastReceived === null &&
          subscriptionCreatedAt !== null &&
          now - subscriptionCreatedAt > INITIAL_DATA_TIMEOUT_MS
        ) {
          console.warn(
            'useSubscriptionHealthCheck: Tab became visible but subscription never received initial data, forcing reconnection',
          );
          forceReconnection();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    appMode,
    isActive,
    forceReconnection,
    cancelPendingRetry,
    subscriptionRef,
    lastDataReceivedRef,
    subscriptionCreatedAtRef,
  ]);

  /**
   * Drop any pending retry when the subscription stops being wanted — the org lost its namespace,
   * or the session left live mode. Without this, a timer scheduled in LIVE would fire minutes
   * later and resubscribe a session that no longer wants the feed. Coming back to live mode is
   * covered by the interval's null-ref check, so nothing is lost by cancelling here.
   */
  useEffect(() => {
    if (isActive && isLiveSubscriptionMode(appMode)) return;
    clearBackoffTimer();
    backoffRef.current.attempt = 0;
  }, [isActive, appMode, clearBackoffTimer]);

  /*
   * Unmount only. Written inline rather than as `useEffect(() => clearBackoffTimer, [...])` so the
   * mount scope is structural: that form is correct ONLY because `clearBackoffTimer` has an empty
   * dep array, and giving it a dependency would silently turn this into "cancel the pending retry
   * whenever that changes" — cancelling a recovery in flight.
   */
  useEffect(
    () => () => {
      if (backoffRef.current.timer) {
        clearTimeout(backoffRef.current.timer);
        backoffRef.current.timer = null;
      }
    },
    [],
  );

  return {
    reconnectTrigger,
    reportSubscriptionError,
    resetSubscriptionBackoff,
    cancelPendingRetry,
  };
};
